import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, Check, ChevronLeft, ChevronRight, Clock, Copy, Lightbulb, MapPin, Star } from 'lucide-react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { bookingApi } from '../../api/bookingApi';
import { servicesApi } from '../../api/servicesApi';
import Footer from '../../components/Footer';
import Header from '../../components/Header';
import LocationAutocomplete from '../../components/LocationAutocomplete';
import PhoneInput from '../../components/PhoneInput';
import ServiceIcon from '../../components/ServiceIcon';
import { useAuth } from '../../context/AuthContext';
import { bookingClientPricing, clientPrice } from '../../utils/pricing';
import { HOUR_STEP, MIN_SERVICE_HOURS, formatHours, isValidBookingHours, roundToHourStep } from '../../utils/duration';

const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const STEPS = ['Service', 'Date', 'Gigger', 'Details', 'Done'];

const pad = (number) => String(number).padStart(2, '0');
const ymd = (year, month, day) => `${year}-${pad(month + 1)}-${pad(day)}`;
const toMin = (time) => {
  const [hours, minutes] = String(time).split(':').map(Number);
  return hours * 60 + minutes;
};
const minToTime = (minutes) => `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`;
const fmt12 = (minutes) => {
  let hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const meridiem = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return `${hours}:${pad(mins)} ${meridiem}`;
};
const fitsInSlots = (slots, startMin, endMin) =>
  (slots || []).some((slot) => toMin(slot.start) <= startMin && toMin(slot.end) >= endMin);
// Every booked service costs at least one hour, so one service is a 1 hr
// minimum and two services a 2 hr minimum. An admin may require more.
const minHoursFor = (serviceOption) =>
  Math.max(MIN_SERVICE_HOURS, Number(serviceOption?.minBookingHours) || 0);

const guideFor = (step, context) => {
  switch (step) {
    case 1:
      return 'Choose the service from the menu and enter your address. We only show Giggers who cover your area.';
    case 2:
      return <>Days highlighted in <strong>green</strong> have Giggers available near {context.suburb || 'you'}.</>;
    case 3:
      return <>Everyone shown covers your area and is available on <strong>{context.date}</strong>. Compare profiles, ratings and rates.</>;
    case 4:
      return <>Choose how many hours you want <strong>{context.name}</strong> for, then add any useful instructions.</>;
    default:
      return null;
  }
};

const Book = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();

  const [step, setStep] = useState(1);
  const [error, setError] = useState('');
  const [services, setServices] = useState([]);
  const [service, setService] = useState(null);
  const [location, setLocation] = useState(null);
  const [calendar, setCalendar] = useState({});
  const [calLoading, setCalLoading] = useState(false);
  const today = useMemo(() => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  }, []);
  const [view, setView] = useState({ year: today.getFullYear(), month: today.getMonth() });
  const [selectedDate, setSelectedDate] = useState(null);
  const [giggers, setGiggers] = useState([]);
  const [giggersLoading, setGiggersLoading] = useState(false);
  const [gigger, setGigger] = useState(null);
  const [startTime, setStartTime] = useState('');
  const [selectedServiceIds, setSelectedServiceIds] = useState([]);
  // Hours the client wants per service. The booking is the sum of these.
  const [serviceHours, setServiceHours] = useState({});
  const [taskNotes, setTaskNotes] = useState('');
  const [client, setClient] = useState({ name: '', email: '', phone: '' });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [pwd, setPwd] = useState('');
  const [pwdBusy, setPwdBusy] = useState(false);
  const [pwdErr, setPwdErr] = useState('');
  const [pwdDone, setPwdDone] = useState(false);
  const [pwdSkipped, setPwdSkipped] = useState(false);

  useEffect(() => {
    let cancelled = false;
    servicesApi
      .list()
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        setServices(list);
        const slug = params.get('service');
        if (slug) {
          const found = list.find((item) => item.slug === slug);
          if (found) setService(found);
        }
      })
      .catch(() => {});

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const chooseService = (serviceId) => {
    const next = services.find((item) => item._id === serviceId) || null;
    setService(next);
    setSelectedServiceIds([]);
    setServiceHours({});
    setTaskNotes('');
    setGigger(null);
    setSelectedDate(null);
    setError('');
  };

  const goToCalendar = async () => {
    setError('');
    if (!service) return setError('Please choose a service.');
    if (!location || location.lat == null) return setError('Please select your location.');

    setStep(2);
    setCalLoading(true);
    try {
      const response = await bookingApi.calendar({
        serviceId: service._id,
        lat: location.lat,
        lng: location.lng,
        postcode: location.postcode,
        days: 60,
      });
      const map = {};
      (response.data || []).forEach((date) => { map[date.date] = date.available; });
      setCalendar(map);
    } catch (err) {
      setError(err.response?.data?.message || "Couldn't load availability.");
    } finally {
      setCalLoading(false);
    }
  };

  const selectDate = async (dateKey) => {
    setError('');
    setSelectedDate(dateKey);
    setStep(3);
    setGiggersLoading(true);
    try {
      const response = await bookingApi.giggers({
        serviceId: service._id,
        lat: location.lat,
        lng: location.lng,
        postcode: location.postcode,
        date: dateKey,
      });
      setGiggers(response.data || []);
    } catch (err) {
      setError(err.response?.data?.message || "Couldn't load Giggers.");
    } finally {
      setGiggersLoading(false);
    }
  };

  const selectGigger = (selectedGigger) => {
    setGigger(selectedGigger);
    setStartTime(selectedGigger.slots?.[0]?.start || '');
    setSelectedServiceIds(service?._id ? [String(service._id)] : []);
    setServiceHours(service?._id ? { [String(service._id)]: minHoursFor(service) } : {});
    setError('');
    setStep(4);
  };

  const bookableServices = useMemo(() => {
    if (!gigger) return [];
    const offered = Array.isArray(gigger.services) ? [...gigger.services] : [];
    const primaryId = String(service?._id || '');
    if (primaryId && !offered.some((item) => String(item._id) === primaryId)) {
      offered.unshift({ ...service, hourlyRate: gigger.hourlyRate });
    }
    return offered;
  }, [gigger, service]);

  const selectedServices = useMemo(() => {
    const selected = new Set(selectedServiceIds.map(String));
    return bookableServices.filter((item) => selected.has(String(item._id)));
  }, [bookableServices, selectedServiceIds]);

  // Hours are the only thing priced, and every booked service is at least one
  // hour: one service is a 1 hr minimum, two services 2 hrs, and so on.
  const hoursForService = (serviceOption, hoursState = serviceHours) => {
    const raw = Number(hoursState[String(serviceOption._id)]);
    return Number.isFinite(raw) && raw > 0 ? raw : minHoursFor(serviceOption);
  };

  const servicePlans = selectedServices.map((item) => ({
    ...item,
    plannedHours: hoursForService(item),
  }));

  const minHours = servicePlans.length > 0
    ? servicePlans.reduce((sum, item) => sum + minHoursFor(item), 0)
    : minHoursFor(service);
  const numHours = servicePlans.reduce((sum, item) => sum + item.plannedHours, 0);
  const safeHours = Number.isFinite(numHours) ? numHours : 0;
  const weightedRateTotal = servicePlans.reduce(
    (sum, item) => sum + ((Number(item.hourlyRate) || 0) * item.plannedHours),
    0
  );
  const baseRate = numHours > 0 ? weightedRateTotal / numHours : (gigger?.hourlyRate || 0);
  const pricingPreview = bookingClientPricing(baseRate, safeHours);
  const total = pricingPreview.clientTotal;
  const deposit = pricingPreview.depositAmount;

  const endTime = useMemo(() => {
    if (!startTime || !Number.isFinite(numHours) || numHours <= 0) return '';
    const end = toMin(startTime) + Math.round(numHours * 60);
    return end <= 24 * 60 ? minToTime(end) : '';
  }, [startTime, numHours]);

  const slotBounds = useMemo(() => {
    const slots = gigger?.slots || [];
    if (!slots.length) return null;
    const min = Math.min(...slots.map((slot) => toMin(slot.start)));
    const max = Math.max(...slots.map((slot) => toMin(slot.end)));
    return { min, max, span: Math.max(max - min, 1) };
  }, [gigger]);

  const bookingBlock = useMemo(() => {
    if (!slotBounds || !startTime || !Number.isFinite(numHours) || numHours <= 0) return null;
    const start = toMin(startTime);
    const end = start + Math.round(numHours * 60);
    const left = ((start - slotBounds.min) / slotBounds.span) * 100;
    const width = ((end - start) / slotBounds.span) * 100;
    return {
      left: Math.max(0, Math.min(100, left)),
      width: Math.max(2, Math.min(100, width)),
      fits: fitsInSlots(gigger.slots, start, end),
    };
  }, [slotBounds, startTime, numHours, gigger]);

  const toggleAdditionalService = (serviceOption) => {
    const id = String(serviceOption._id);
    if (id === String(service?._id)) return;
    const removing = selectedServiceIds.includes(id);
    const nextIds = removing
      ? selectedServiceIds.filter((item) => item !== id)
      : [...selectedServiceIds, id];
    const nextHours = { ...serviceHours };
    if (removing) {
      delete nextHours[id];
    } else {
      nextHours[id] = minHoursFor(serviceOption);
    }
    setSelectedServiceIds(nextIds);
    setServiceHours(nextHours);
    setError('');
  };

  const updateServiceHours = (serviceOption, value) => {
    setServiceHours({ ...serviceHours, [String(serviceOption._id)]: value === '' ? '' : Number(value) });
    setError('');
  };

  // Snap to a half-hour step no lower than this service's minimum, so the field
  // can never be left showing a value the booking would reject.
  const commitServiceHours = (serviceOption) => {
    const floor = minHoursFor(serviceOption);
    const raw = Number(serviceHours[String(serviceOption._id)]);
    const next = Math.max(floor, roundToHourStep(Number.isFinite(raw) ? raw : floor));
    setServiceHours({ ...serviceHours, [String(serviceOption._id)]: next });
  };

  const submit = async () => {
    setError('');
    if (selectedServices.length === 0) return setError('Please keep at least one service in this booking.');
    if (!isValidBookingHours(numHours)) return setError('Enter the booking time in half-hour steps, for example 1, 1.5 or 2 hrs.');
    if (numHours < minHours) return setError(`Minimum booking for ${selectedServices.length === 1 ? 'this service' : `these ${selectedServices.length} services`} is ${formatHours(minHours)}.`);
    const startMin = toMin(startTime);
    const endMin = startMin + Math.round(numHours * 60);
    if (endMin > 24 * 60) return setError('That would run past midnight — shorten the hours or start earlier.');
    if (!fitsInSlots(gigger.slots, startMin, endMin)) return setError("The chosen time is outside the Gigger's available hours that day.");
    if (!client.name.trim()) return setError('Please enter your name.');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(client.email)) return setError('Please enter a valid email.');

    setSubmitting(true);
    try {
      const response = await bookingApi.submit({
        serviceId: service._id,
        giggerId: gigger._id,
        date: selectedDate,
        startTime,
        hours: numHours,
        // Sub services are no longer chosen at booking time — the client books
        // the service for a number of hours and nothing else.
        tasks: [],
        services: servicePlans.map((item) => ({
          serviceId: item._id,
          hours: item.plannedHours,
        })),
        taskNotes: taskNotes.trim(),
        client: {
          name: client.name.trim(),
          email: client.email.trim(),
          phone: client.phone.trim(),
        },
        location: {
          address: location.address,
          suburb: location.suburb,
          state: location.state,
          postcode: location.postcode,
          lat: location.lat,
          lng: location.lng,
        },
      });
      setResult(response.data);
      setStep(5);
    } catch (err) {
      setError(err.response?.data?.message || "Couldn't submit the booking.");
    } finally {
      setSubmitting(false);
    }
  };

  const copyId = () => {
    if (!result?.trackingId) return;
    navigator.clipboard?.writeText(result.trackingId);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const setPassword = async () => {
    setPwdErr('');
    if (pwd.length < 8) return setPwdErr('Password must be at least 8 characters.');
    setPwdBusy(true);
    try {
      await bookingApi.activate({ email: client.email.trim(), trackingId: result.trackingId, password: pwd });
      const user = await login(client.email.trim(), pwd);
      if (user) return navigate('/client/dashboard');
      setPwdDone(true);
    } catch (err) {
      setPwdErr(err.response?.data?.message || "Couldn't set your password.");
    } finally {
      setPwdBusy(false);
    }
  };

  const firstDow = (new Date(view.year, view.month, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(view.year, view.month + 1, 0).getDate();
  const atCurrentMonth = view.year === today.getFullYear() && view.month === today.getMonth();
  const prevMonth = () => setView((current) => current.month === 0 ? { year: current.year - 1, month: 11 } : { year: current.year, month: current.month - 1 });
  const nextMonth = () => setView((current) => current.month === 11 ? { year: current.year + 1, month: 0 } : { year: current.year, month: current.month + 1 });

  const fmtNiceDate = (key) => {
    if (!key) return '';
    const [year, month, day] = key.split('-').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString('en-AU', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    });
  };

  const guide = guideFor(step, {
    suburb: location?.suburb,
    date: fmtNiceDate(selectedDate),
    name: gigger?.name,
  });

  const inputClass = 'box-border w-full rounded-2xl border-0 bg-slate-100/80 px-4 py-3.5 text-[15px] text-slate-950 outline-none ring-1 ring-inset ring-slate-200 transition-all duration-200 placeholder:text-slate-400 hover:bg-white hover:ring-slate-300 focus:bg-white focus:ring-2 focus:ring-green-500 focus:shadow-[0_8px_24px_rgba(22,101,52,0.10)]';
  const primaryButton = 'cursor-pointer rounded-2xl border-0 bg-green-700 px-5 py-3.5 text-sm font-bold text-white shadow-[0_8px_22px_rgba(21,128,61,0.16)] transition-all duration-200 hover:-translate-y-0.5 hover:bg-green-800 hover:shadow-[0_12px_28px_rgba(21,128,61,0.22)] active:translate-y-0 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0';

  return (
    <>
      <Header />
      <title>Book a Service | GIGGS</title>

      <main className="min-h-[70vh] overflow-x-hidden bg-slate-50 py-8 text-slate-900 sm:py-10 lg:py-12">
        <div className="mx-auto w-full max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="mb-7 sm:mb-8">
            <div className="grid grid-cols-5 items-start">
              {STEPS.map((label, index) => {
                const number = index + 1;
                const done = step > number;
                const active = step === number;
                return (
                  <div key={label} className="relative flex min-w-0 flex-col items-center text-center">
                    {index < STEPS.length - 1 && <span className={`absolute left-1/2 top-4 h-0.5 w-full ${step > number ? 'bg-green-600' : 'bg-slate-200'}`} />}
                    <span className={`relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-black ${done || active ? 'bg-green-700 text-white' : 'bg-white text-slate-400 ring-1 ring-slate-200'}`}>{done ? <Check size={14} strokeWidth={3} /> : number}</span>
                    <span className={`relative z-10 mt-2 max-w-full break-words bg-slate-50 px-0.5 text-[9px] font-bold leading-tight sm:px-1 sm:text-[11px] ${active ? 'text-green-700' : 'text-slate-500'}`}>{label}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {guide && <div className="mb-5 flex items-start gap-3 rounded-2xl border border-green-100 bg-green-50 p-4 text-sm leading-6 text-green-900"><Lightbulb size={18} className="mt-0.5 shrink-0 text-green-700" /><div className="min-w-0 break-words">{guide}</div></div>}
          {error && <div className="mb-5 break-words rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">{error}</div>}

          <section className="rounded-3xl border border-slate-200 bg-white p-4 shadow-[0_12px_35px_rgba(15,23,42,0.07)] sm:p-6 lg:p-8">
            {step === 1 && (
              <div>
                <h1 className="m-0 text-[clamp(1.75rem,5vw,2.25rem)] font-black leading-tight tracking-[-0.025em] text-slate-950">What do you need help with?</h1>
                <p className="mb-0 mt-2 text-sm leading-6 text-slate-600">Choose a service and tell us where you need it.</p>

                <label className="mb-2 mt-6 block text-xs font-bold uppercase tracking-[0.12em] text-slate-600 sm:mt-7" htmlFor="service-select">Service</label>
                <select id="service-select" value={service?._id || ''} onChange={(event) => chooseService(event.target.value)} className={`${inputClass} cursor-pointer appearance-auto bg-white`}>
                  <option value="">Select a service…</option>
                  {services.filter((item) => item.status === 'Active').map((item) => <option key={item._id} value={item._id}>{item.name}</option>)}
                </select>
                {service && (
                  <div className="mt-3 flex min-w-0 items-center gap-3 rounded-2xl border border-green-100 bg-green-50 p-3.5">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-green-700"><ServiceIcon serviceId={service.icon || service.slug} size={21} /></span>
                    <div className="min-w-0"><div className="break-words text-sm font-bold text-slate-950">{service.name}</div>{service.description && <div className="mt-0.5 break-words text-xs leading-5 text-slate-600">{service.description}</div>}</div>
                  </div>
                )}

                <label className="mb-2 mt-6 block text-xs font-bold uppercase tracking-[0.12em] text-slate-600 sm:mt-7">Your location</label>
                <LocationAutocomplete apiKey={MAPS_KEY} defaultValue={location?.address || ''} onSelect={(selected) => setLocation(selected)} />
                {location?.suburb && <div className="mt-2 flex items-start gap-2 text-xs font-semibold text-green-700"><MapPin size={14} className="mt-0.5 shrink-0" /><span className="min-w-0 break-words">{[location.suburb, location.state, location.postcode].filter(Boolean).join(' ')}</span></div>}

                <button type="button" onClick={goToCalendar} disabled={!service || !location} className={`${primaryButton} mt-7 w-full`}>See availability</button>
              </div>
            )}

            {step === 2 && (
              <div>
                <button type="button" onClick={() => setStep(1)} className="inline-flex cursor-pointer items-center gap-2 border-0 bg-transparent p-0 text-sm font-bold text-green-700"><ArrowLeft size={16} /> Back</button>
                <h1 className="mb-0 mt-5 text-[clamp(1.75rem,5vw,2.25rem)] font-black leading-tight tracking-[-0.025em] text-slate-950">Pick a date</h1>
                <p className="mb-0 mt-2 break-words text-sm leading-6 text-slate-600">When would you like {service?.name} in {location?.suburb || 'your area'}?</p>

                <div className="mt-6 flex items-center justify-between gap-3 sm:mt-7">
                  <button type="button" onClick={prevMonth} disabled={atCurrentMonth} aria-label="Previous month" className="flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700 disabled:cursor-not-allowed disabled:opacity-30"><ChevronLeft size={18} /></button>
                  <div className="min-w-0 text-center text-sm font-black text-slate-950 sm:text-base">{MONTHS[view.month]} {view.year}</div>
                  <button type="button" onClick={nextMonth} aria-label="Next month" className="flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700"><ChevronRight size={18} /></button>
                </div>

                {calLoading ? (
                  <div className="flex flex-col items-center py-12 text-center sm:py-16"><span className="h-8 w-8 animate-spin rounded-full border-4 border-green-100 border-t-green-700" /><h2 className="mb-0 mt-4 text-base font-bold text-slate-950">Checking availability...</h2><p className="mb-0 mt-1 text-sm text-slate-500">Finding open days near {location?.suburb || 'you'}</p></div>
                ) : (
                  <>
                    <div className="mt-5 grid grid-cols-7 gap-1 sm:mt-6 sm:gap-2">
                      {DAY_LABELS.map((label) => <div key={label} className="py-2 text-center text-[9px] font-bold uppercase tracking-wide text-slate-400 sm:text-[11px]">{label}</div>)}
                      {Array.from({ length: firstDow }).map((_, index) => <div key={`blank-${index}`} />)}
                      {Array.from({ length: daysInMonth }).map((_, index) => {
                        const dayNum = index + 1;
                        const key = ymd(view.year, view.month, dayNum);
                        const cellDate = new Date(view.year, view.month, dayNum);
                        const isPast = cellDate < today;
                        const isToday = cellDate.getTime() === today.getTime();
                        const open = calendar[key] === true && !isPast;
                        const selected = selectedDate === key;
                        const className = `aspect-square min-w-0 rounded-lg border text-xs font-bold transition sm:rounded-xl sm:text-sm ${selected ? 'border-green-700 bg-green-700 text-white' : open ? 'cursor-pointer border-green-200 bg-green-50 text-green-800 hover:bg-green-100' : 'cursor-not-allowed border-slate-100 bg-slate-50 text-slate-300'} ${isToday && !selected ? 'ring-2 ring-green-200' : ''}`;
                        return <button key={key} type="button" className={className} disabled={!open} onClick={() => open && selectDate(key)}>{dayNum}</button>;
                      })}
                    </div>
                    <div className="mt-5 flex flex-wrap gap-4 text-xs text-slate-500"><span className="flex items-center gap-2"><i className="h-2.5 w-2.5 rounded-full bg-green-500" /> Giggers available</span><span className="flex items-center gap-2"><i className="h-2.5 w-2.5 rounded-full bg-slate-200" /> None in your area</span></div>
                  </>
                )}
              </div>
            )}

            {step === 3 && (
              <div>
                <button type="button" onClick={() => setStep(2)} className="inline-flex cursor-pointer items-center gap-2 border-0 bg-transparent p-0 text-sm font-bold text-green-700"><ArrowLeft size={16} /> Back</button>
                <h1 className="mb-0 mt-5 text-[clamp(1.75rem,5vw,2.25rem)] font-black leading-tight tracking-[-0.025em] text-slate-950">Choose your Gigger</h1>
                <p className="mb-0 mt-2 break-words text-sm leading-6 text-slate-600">Available {fmtNiceDate(selectedDate)} for {service?.name}.</p>

                {giggersLoading ? (
                  <div className="py-12 text-center sm:py-14"><span className="mx-auto block h-8 w-8 animate-spin rounded-full border-4 border-green-100 border-t-green-700" /><h2 className="mb-0 mt-4 text-base font-bold text-slate-950">Looking for Giggers in your area...</h2><p className="mb-0 mt-1 text-sm text-slate-500">Matching available {service?.name} providers</p></div>
                ) : giggers.length === 0 ? (
                  <div className="mt-7 rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-5 text-center sm:p-8"><h2 className="m-0 text-lg font-bold text-slate-950">No Giggers are free that day</h2><p className="mb-0 mt-2 text-sm text-slate-500">Try another date. Availability changes regularly.</p><button type="button" onClick={() => setStep(2)} className="mt-5 cursor-pointer rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-800">Choose another date</button></div>
                ) : (
                  <div className="mt-7 space-y-3">
                    {giggers.map((item) => {
                      const isNew = item.reviewCount === 0 && item.completedJobs === 0;
                      return (
                        <article key={item._id} className="min-w-0 rounded-2xl border border-slate-200 p-4 transition hover:border-green-300 hover:shadow-sm">
                          <div className="flex min-w-0 flex-col gap-4 sm:flex-row sm:items-center">
                            <button type="button" onClick={() => selectGigger(item)} className="flex min-w-0 flex-1 cursor-pointer items-center gap-3 border-0 bg-transparent p-0 text-left sm:gap-4">
                              <span className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-full bg-green-700 text-lg font-black text-white sm:h-14 sm:w-14">{item.avatar ? <img src={item.avatar} alt={item.name} className="h-full w-full object-cover" /> : (item.name || '?').charAt(0).toUpperCase()}</span>
                              <span className="min-w-0 flex-1">
                                <span className="flex min-w-0 flex-wrap items-center gap-2"><span className="min-w-0 break-words text-base font-black text-slate-950">{item.name}</span>{isNew && <span className="shrink-0 rounded-full bg-green-50 px-2 py-0.5 text-[11px] font-bold text-green-700">New</span>}</span>
                                <span className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500">{!isNew && <span className="inline-flex items-center gap-1"><Star size={13} fill="currentColor" className="text-amber-500" /> {item.ratingAvg} ({item.reviewCount})</span>}{item.completedJobs > 0 && <span>{item.completedJobs} jobs</span>}{item.distanceKm != null && <span>{item.distanceKm} km away</span>}</span>
                                {(item.slots || []).length > 0 && <span className="mt-2 flex flex-wrap gap-1.5">{item.slots.map((slot, index) => <span key={index} className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-600">{fmt12(toMin(slot.start))} – {fmt12(toMin(slot.end))}</span>)}</span>}
                              </span>
                            </button>
                            <div className="flex flex-wrap items-center justify-between gap-3 sm:ml-auto sm:flex-col sm:items-end">
                              {item.hourlyRate != null && <div className="text-left sm:text-right"><div className="text-lg font-black text-slate-950">${clientPrice(item.hourlyRate)}</div><div className="text-[11px] text-slate-500">per hour</div></div>}
                              <div className="flex flex-wrap gap-2"><Link to={`/gigger/${item._id}`} target="_blank" onClick={(event) => event.stopPropagation()} className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-bold text-slate-700 no-underline hover:bg-slate-50">View profile</Link><button type="button" onClick={() => selectGigger(item)} className="cursor-pointer rounded-lg border-0 bg-green-700 px-3 py-2 text-xs font-bold text-white hover:bg-green-800">Choose</button></div>
                            </div>
                          </div>
                        </article>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {step === 4 && gigger && (
              <div>
                <button type="button" onClick={() => setStep(3)} className="inline-flex cursor-pointer items-center gap-2 border-0 bg-transparent p-0 text-sm font-bold text-green-700"><ArrowLeft size={16} /> Back</button>
                <h1 className="mb-0 mt-5 text-[clamp(1.75rem,5vw,2.25rem)] font-black leading-tight tracking-[-0.025em] text-slate-950">Booking details</h1>
                <p className="mb-0 mt-2 break-words text-sm leading-6 text-slate-600">{gigger.name} · {fmtNiceDate(selectedDate)}</p>

                <section className="mt-6 rounded-2xl border border-slate-200 bg-slate-50/60 p-4 sm:mt-7 sm:p-5">
                  <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between sm:gap-4">
                    <div><h2 className="m-0 text-base font-black text-slate-950">Services for this booking</h2><p className="mb-0 mt-1 text-xs leading-5 text-slate-500">Your first service is included. Add any other service {gigger.name} offers, then set how many hours you want for each. Every service is at least {formatHours(MIN_SERVICE_HOURS)}.</p></div>
                    <div className="shrink-0 text-xs font-bold text-green-700">Booking total: {formatHours(numHours)}</div>
                  </div>

                  <div className="mt-4 grid gap-2 sm:grid-cols-2">
                    {bookableServices.map((item) => {
                      const isPrimary = String(item._id) === String(service?._id);
                      const checked = selectedServiceIds.includes(String(item._id));
                      return (
                        <label key={item._id} className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3.5 transition ${checked ? 'border-green-300 bg-green-50' : 'border-slate-200 bg-white hover:border-green-200'}`}>
                          <input type="checkbox" checked={checked} disabled={isPrimary} onChange={() => toggleAdditionalService(item)} className="h-4 w-4 shrink-0 accent-green-700 disabled:opacity-70" />
                          <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-green-700/10 text-green-700"><ServiceIcon serviceId={item.icon || item.slug} size={17} /></span>
                          <span className="min-w-0 flex-1"><span className="block break-words text-sm font-bold text-slate-800">{item.name}</span><span className="mt-0.5 block text-[11px] font-semibold text-slate-500">${clientPrice(item.hourlyRate || 0)}/hr · min {formatHours(minHoursFor(item))}</span></span>
                          {isPrimary && <span className="rounded-full bg-green-700 px-2 py-1 text-[9px] font-black uppercase tracking-wide text-white">Primary</span>}
                        </label>
                      );
                    })}
                  </div>

                  <div className="mt-5 space-y-3">
                    {selectedServices.map((serviceOption) => {
                      const serviceMin = minHoursFor(serviceOption);
                      const rawHours = serviceHours[String(serviceOption._id)];
                      return (
                        <div key={serviceOption._id} className="flex flex-wrap items-end justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-4">
                          <div className="min-w-0">
                            <h3 className="m-0 text-sm font-black text-slate-900">{serviceOption.name}</h3>
                            <p className="mb-0 mt-1 text-[11px] text-slate-500">${clientPrice(serviceOption.hourlyRate || 0)}/hr · minimum {formatHours(serviceMin)}</p>
                          </div>
                          <label className="w-[150px] shrink-0">
                            <span className="mb-1.5 block text-[11px] font-bold text-slate-700">Hours</span>
                            <input
                              type="number"
                              min={serviceMin}
                              step={HOUR_STEP}
                              inputMode="decimal"
                              value={rawHours === undefined ? serviceMin : rawHours}
                              onChange={(event) => updateServiceHours(serviceOption, event.target.value)}
                              onBlur={() => commitServiceHours(serviceOption)}
                              aria-label={`${serviceOption.name} hours`}
                              className={`${inputClass} !py-2.5 text-center`}
                            />
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </section>
                <label className="mt-5 block min-w-0">
                  <span className="mb-2 block text-xs font-bold text-slate-700">Job details / special instructions <span className="font-medium text-slate-400">(optional)</span></span>
                  <textarea className={`${inputClass} min-h-28 resize-y`} maxLength={1500} value={taskNotes} onChange={(event) => setTaskNotes(event.target.value)} rows={4} placeholder="Add priorities and useful details such as rooms/items, access, parking, pets, equipment, products or the outcome you expect." />
                  <span className="mt-1.5 block text-right text-[10px] text-slate-400">{taskNotes.length}/1500</span>
                </label>

                <div className="mt-5 rounded-2xl border border-green-100 bg-green-50 p-4 sm:p-5">
                  <div className="flex items-start gap-2 text-sm font-bold text-green-900"><Clock size={17} className="mt-0.5 shrink-0" /><span className="min-w-0 break-words">When {gigger.name} is free that day</span></div>
                  <div className="mt-3 flex flex-wrap gap-2">{(gigger.slots || []).length ? gigger.slots.map((slot, index) => <span key={index} className="rounded-full bg-white px-3 py-1.5 text-xs font-bold text-green-800">{fmt12(toMin(slot.start))} – {fmt12(toMin(slot.end))}</span>) : <span className="text-xs text-green-800">No hours listed</span>}</div>
                  {slotBounds && <><div className="relative mt-5 h-8 overflow-hidden rounded-lg bg-white">{(gigger.slots || []).map((slot, index) => { const left = ((toMin(slot.start) - slotBounds.min) / slotBounds.span) * 100; const width = ((toMin(slot.end) - toMin(slot.start)) / slotBounds.span) * 100; return <div key={index} className="absolute top-0 h-full bg-green-200" style={{ left: `${left}%`, width: `${width}%` }} />; })}{bookingBlock && <div className={`absolute bottom-1 top-1 flex items-center justify-center overflow-hidden rounded-md px-1 text-[10px] font-black text-white ${bookingBlock.fits ? 'bg-green-700' : 'bg-red-500'}`} style={{ left: `${bookingBlock.left}%`, width: `${bookingBlock.width}%` }}>{bookingBlock.width > 22 ? 'Your booking' : ''}</div>}</div><div className="mt-1 flex justify-between text-[10px] font-semibold text-green-800 sm:text-[11px]"><span>{fmt12(slotBounds.min)}</span><span>{fmt12(slotBounds.max)}</span></div>{bookingBlock && !bookingBlock.fits && <div className="mt-3 break-words rounded-lg bg-red-50 px-3 py-2 text-xs font-semibold text-red-700">That time runs outside {gigger.name}'s hours. Adjust the start time or booking length.</div>}</>}
                </div>

                <div className="mt-5 grid gap-4 sm:grid-cols-2">
                  <label className="block min-w-0"><span className="mb-2 block text-xs font-bold text-slate-700">Start time</span><input type="time" value={startTime} onChange={(event) => setStartTime(event.target.value)} className={inputClass} /></label>
                  <div className="block min-w-0">
                    <span className="mb-2 block text-xs font-bold text-slate-700">Booking time</span>
                    <div className={`${inputClass} flex items-center font-bold text-slate-900`}>{formatHours(numHours)}</div>
                    <span className="mt-1.5 block text-[11px] font-semibold text-slate-500">Set above, per service. Minimum {formatHours(minHours)} for {selectedServices.length === 1 ? 'this service' : `these ${selectedServices.length} services`}.</span>
                  </div>
                </div>
                {endTime && <p className="mb-0 mt-2 text-xs font-semibold text-slate-500">Estimated finish: {fmt12(toMin(endTime))}</p>}

                <div className="mt-6 grid gap-4 sm:grid-cols-2">
                  <label className="block min-w-0 sm:col-span-2"><span className="mb-2 block text-xs font-bold text-slate-700">Your name</span><input value={client.name} onChange={(event) => setClient({ ...client, name: event.target.value })} className={inputClass} autoComplete="name" /></label>
                  <label className="block min-w-0"><span className="mb-2 block text-xs font-bold text-slate-700">Email</span><input type="email" value={client.email} onChange={(event) => setClient({ ...client, email: event.target.value })} className={inputClass} autoComplete="email" /></label>
                  <label className="block min-w-0"><span className="mb-2 block text-xs font-bold text-slate-700">Phone</span><PhoneInput className={inputClass} value={client.phone} onChange={(phone) => setClient((current) => ({ ...current, phone }))} required /></label>
                </div>

                <div className="mt-6 rounded-2xl bg-slate-950 p-4 text-white sm:p-5">
                  <div className="flex flex-wrap items-center justify-between gap-4"><span className="text-sm font-semibold text-slate-300">Estimated total</span><span className="text-2xl font-black text-white">${total.toFixed(2)}</span></div>
                  <div className="my-4 h-px bg-white/10" />
                  <div className="flex flex-wrap items-center justify-between gap-4"><span className="min-w-0 flex-1 text-xs text-slate-400">100% booking payment due once your Gigger accepts</span><span className="shrink-0 text-sm font-bold text-green-300">${deposit.toFixed(2)}</span></div>
                </div>

                <button type="button" onClick={submit} disabled={submitting} className={`${primaryButton} mt-6 w-full`}>{submitting ? 'Submitting...' : 'Confirm booking'}</button>
                <p className="mb-0 mt-3 text-center text-xs leading-5 text-slate-500">No payment now. {gigger.name} has 4 business hours to accept; once accepted, the full booking amount is due.</p>
              </div>
            )}

            {step === 5 && result && (
              <div className="text-center">
                <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-green-100 text-green-700"><Check size={34} strokeWidth={3} /></span>
                <h1 className="mb-0 mt-5 text-[clamp(1.75rem,5vw,2.25rem)] font-black leading-tight text-slate-950">Booking submitted</h1>
                <p className="mx-auto mb-0 mt-3 max-w-lg text-sm leading-6 text-slate-600">{gigger?.name} has 4 business hours to accept. Save your tracking ID so you can manage this booking.</p>

                <div className="mx-auto mt-7 max-w-lg rounded-2xl border border-green-200 bg-green-50 p-4 text-left sm:p-5"><span className="text-xs font-bold uppercase tracking-[0.12em] text-green-700">Your tracking ID</span><div className="mt-2 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"><span className="min-w-0 break-all font-mono text-lg font-black text-slate-950">{result.trackingId}</span><button type="button" onClick={copyId} className="inline-flex shrink-0 cursor-pointer items-center justify-center gap-2 rounded-lg border border-green-200 bg-white px-3 py-2 text-xs font-bold text-green-800"><Copy size={14} /> {copied ? 'Copied' : 'Copy'}</button></div></div>

                {result.account && !result.account.hasPassword && !pwdSkipped && !pwdDone && <div className="mx-auto mt-5 max-w-lg rounded-2xl border border-slate-200 bg-slate-50 p-4 text-left sm:p-5"><h2 className="m-0 text-base font-bold text-slate-950">Manage all your bookings</h2><p className="mb-0 mt-2 text-sm leading-6 text-slate-600">Set a password to sign in and see everything in one place, or skip and keep using your tracking ID.</p><div className="mt-4 flex flex-col gap-2 sm:flex-row"><input type="password" value={pwd} onChange={(event) => setPwd(event.target.value)} placeholder="Choose a password" className={inputClass} /><button type="button" onClick={setPassword} disabled={pwdBusy} className={`${primaryButton} shrink-0 sm:px-5`}>{pwdBusy ? '...' : 'Save'}</button></div>{pwdErr && <div className="mt-2 break-words text-sm font-semibold text-red-600">{pwdErr}</div>}<button type="button" onClick={() => setPwdSkipped(true)} className="mt-3 cursor-pointer border-0 bg-transparent p-0 text-xs font-bold text-slate-500 hover:text-slate-700">Skip for now</button></div>}
                {pwdDone && <div className="mx-auto mt-5 max-w-lg rounded-2xl border border-green-200 bg-green-50 p-4 text-sm text-green-800">Password set. <Link to="/login" className="font-bold text-green-800">Log in</Link> to manage your bookings.</div>}


                <div className="mx-auto mt-5 max-w-lg rounded-2xl border border-slate-200 p-4 text-left sm:p-5"><div className="flex flex-wrap items-center justify-between gap-4"><span className="text-sm font-semibold text-slate-600">Total (estimated)</span><span className="text-xl font-black text-slate-950">${Number(result.pricing?.clientTotal ?? total).toFixed(2)}</span></div><p className="mb-0 mt-2 text-xs text-slate-500">Final booking amount shown as one merged total.</p></div>

                <div className="mt-7 flex flex-col justify-center gap-3 sm:flex-row"><button type="button" onClick={() => navigate(`/track/${result.trackingId}`)} className={`${primaryButton} sm:w-auto`}>Track your booking</button><button type="button" onClick={() => navigate('/')} className="cursor-pointer rounded-2xl border border-slate-200 bg-white px-5 py-3.5 text-sm font-bold text-slate-800 transition hover:bg-slate-50">Back to home</button></div>
              </div>
            )}
          </section>
        </div>
      </main>

      <Footer />
    </>
  );
};

export default Book;